import platform, { analytics } from '@play-co/gcinstant';
import { ShareAsyncOptions } from '@play-co/gcinstant';
import { withTiming } from '@play-co/gcinstant/build/esm/asyncUtils';
import { share } from '@play-co/gcinstant/build/esm/web/share';
import { LinkRefData } from 'src/replicant/chatbot';
import StateObserver from 'src/StateObserver';
import { encode } from 'src/replicant/utils/base64';

interface InstagramShareOptions extends ShareAsyncOptions {
  /** Configures the introductory play button message sent to users who click this share. */
  chatbot?: {
    messageText?: string;
    imageUrl?: string;
    buttonText?: string;
  };

  /** Whether to share using the native share sheet (navigator.share) or directly to Messenger (intent URL). */
  method: 'native' | 'direct';
}

export function createLinkToInstagram(refData: LinkRefData): string {
  let urlStr = 'https://ig.me/m/tremblygame';

  if (process.env.IS_DEVELOPMENT) {
    urlStr = 'https://ig.me/m/decoratingdivagame';
    // urlStr = 'https://ig.me/m/wordfrenzygame';
  }

  const url = new URL(urlStr);
  url.searchParams.set('ref', encode(JSON.stringify(refData)));
  return url.href;
}

export function redirectToInstagram() {
  const link = createLinkToInstagram({
    type: 'LANDING',
  });
  location.href = link;
}

function getRandomLinkId(length = 8): string {
  const base62 =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  let str = '';
  for (let ii = 0; ii < length; ++ii) {
    str += base62.charAt((Math.random() * 62) >>> 0);
  }
  return str;
}

function createShortUrl(linkId: string, body: unknown): string {
  // The domain to use for the short link
  const shortDomain = !location.port
    ? location.hostname
    : 'localhost.playco.games';

  // The short URL we're about to create
  const shortUrl = `https://${shortDomain}/share/${linkId}`;

  // Post to the backend in the background
  fetch(shortUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch((error) => {
    analytics.pushError('WebCreateShortURLFailure', error, {
      webShareLinkId: linkId,
    });
  });

  return shortUrl;
}

/** Shares a link that redirects to the Instagram chatbot, with Opengraph and virality analytics. */
export async function shareLinkToInstagram(
  opts: InstagramShareOptions,
): Promise<void> {
  const userId = StateObserver.getState().user.id;
  const fullPayload = (platform as any)._populateNotificationPayload(
    opts.data,
    'INVITE',
  );
  const linkId = getRandomLinkId();

  const payload = {
    playerID: fullPayload.playerID,
    feature: fullPayload.feature,
    $subFeature: fullPayload.$subFeature,
    $channel: fullPayload.$channel,
    webShareLinkId: linkId,
  };

  // Create the long ig.me link
  const longUrl = createLinkToInstagram({
    type: 'SHARE',
    payload,
    messageText: opts.chatbot?.messageText,
    imageUrl: opts.chatbot?.imageUrl,
    buttonText: opts.chatbot?.buttonText,
  });
  console.log('Long URL:', longUrl);

  // Create the webview escape redirect link (currently for IG only)
  const escapeUrl = new URL('https://frxk7e0wmj.execute-api.us-east-1.amazonaws.com/escape');
  escapeUrl.searchParams.set('url', longUrl);
  console.log('Webview escape URL:', escapeUrl);

  // Create the short mygame.com/share/abc123 link
  const shortUrl = createShortUrl(linkId, {
    url: escapeUrl,
    // Convert relative URL paths to absolute paths
    image: opts.image ? new URL(opts.image, location.href).href : undefined,
    title: opts.title,
    description: opts.description,
    amplitudeId: process.env.AMPLITUDE,
    userId,
    eventProps: payload,
  });
  console.log('Short URL:', shortUrl);

  analytics.pushEvent('ShareAsync', payload);

  try {
    const { requestMs } = await withTiming(() =>
      share({ text: opts.text, url: shortUrl }),
    );
    analytics.pushEvent('ShareAsyncSuccess', { ...payload, requestMs });
  } catch ({ error, requestMs }) {
    analytics.pushError('ShareAsyncFailed', error, { ...payload, requestMs });
    throw error;
  }
}
/** Shares the long link that redirects to the Instagram chatbot, as alternative to regular short links */
export async function shareLongLinkToInstagram(
  opts: InstagramShareOptions,
): Promise<void> {
  const fullPayload = (platform as any)._populateNotificationPayload(
    opts.data,
    'INVITE',
  );
  const linkId = getRandomLinkId();

  const payload = {
    playerID: fullPayload.playerID,
    feature: fullPayload.feature,
    $subFeature: fullPayload.$subFeature,
    $channel: fullPayload.$channel,
    webShareLinkId: linkId,
  };

  // Create the long ig.me link
  const longUrl = createLinkToInstagram({
    type: 'SHARE',
    payload,
    messageText: opts.chatbot?.messageText,
    imageUrl: opts.chatbot?.imageUrl,
    buttonText: opts.chatbot?.buttonText,
  });
  console.log('Long URL:', longUrl);

  analytics.pushEvent('ShareAsync', payload);

  try {
    const { requestMs } = await withTiming(() =>
      share({ text: opts.text, url: longUrl }),
    );
    analytics.pushEvent('ShareAsyncSuccess', { ...payload, requestMs });
  } catch ({ error, requestMs }) {
    analytics.pushError('ShareAsyncFailed', error, { ...payload, requestMs });
    throw error;
  }
}
